import { ref, computed, watch } from 'vue'

export const DEFAULT_OPS = ['contains', 'is', 'is not', 'starts with', 'is empty', 'is not empty']

// Date-specific operators. Use on columns whose raw value is an ISO date/datetime string.
export const DATE_OPS = ['after', 'before', 'on', 'today', 'last 7 days', 'last 30 days', 'month to date', 'last month', 'quarter to date', 'last 3 months', 'year to date', 'next 7 days', 'this week', 'is empty', 'is not empty']

// Numeric operators. Use on columns whose filterValue returns a numeric string.
export const NUMBER_OPS = ['=', '!=', '>', '<', '>=', '<=', 'is empty', 'is not empty']

// Operators that need no value input
export const NO_VALUE_OPS = new Set(['is empty', 'is not empty', 'today', 'last 7 days', 'last 30 days', 'month to date', 'last month', 'quarter to date', 'last 3 months', 'year to date', 'next 7 days', 'this week'])

// Operators whose value is a {start, end} date range checked against a column-supplied
// col.rangeCheck(row, {start, end}) predicate instead of a plain scalar comparison — for
// columns backed by a one-to-many relationship (e.g. "has lessons in range") rather than
// a single field on the row.
export const RANGE_OPS = new Set(['has any in range', 'has none in range'])

// Operators that want a <input type="date"> instead of free text
export const DATE_INPUT_OPS = new Set(['before', 'after', 'on'])

// Operators that want a <input type="number"> instead of free text
export const NUMBER_INPUT_OPS = new Set(['=', '!=', '>', '<', '>=', '<='])

const _DATE_OP_SET   = new Set(['before', 'after', 'on', 'today', 'last 7 days', 'last 30 days', 'month to date', 'last month', 'quarter to date', 'last 3 months', 'year to date', 'next 7 days', 'this week'])
const _NUMBER_OP_SET = new Set(['=', '!=', '>', '<', '>=', '<='])

// ── Date helpers ──────────────────────────────────────────────────────────────
function _isoToday() {
  return new Date().toISOString().split('T')[0]
}

function _isoOffset(days) {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString().split('T')[0]
}

function _isoYearStart() {
  return `${new Date().getFullYear()}-01-01`
}

function _isoQuarterStart() {
  const d = new Date()
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1).toISOString().split('T')[0]
}

function _isoMonthStart() {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]
}

function _isoLastMonthStart() {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().split('T')[0]
}

function _isoLastMonthEnd() {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().split('T')[0]
}

function _isoNMonthsAgoStart(n) {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth() - n, 1).toISOString().split('T')[0]
}

// Extract ISO date string "YYYY-MM-DD" from a row for date operators.
// Falls back to slicing the raw column value (works for both Date and DateTime strings).
function getDateValue(col, row) {
  if (col.dateValue) return col.dateValue(row) || ''
  const v = String(row[col.key] ?? '')
  return v.slice(0, 10)
}

// ── Filter logic ──────────────────────────────────────────────────────────────
function getFilterableValue(col, row) {
  if (col.filterValue) return col.filterValue(row)
  if (col.compute)     return String(col.compute(row))
  return String(row[col.key] ?? '')
}

function getSortableValue(col, row) {
  if (col.sortValue) return col.sortValue(row)
  const v = getFilterableValue(col, row)
  return typeof v === 'number' ? v : v.toLowerCase()
}

function applyFilter(col, f, row) {
  // Range operators: delegate to the column's own rangeCheck, not a scalar compare
  if (RANGE_OPS.has(f.op)) {
    const inRange = col.rangeCheck ? col.rangeCheck(row, f.value || {}) : false
    return f.op === 'has any in range' ? inRange : !inRange
  }

  // Date operators: compare on raw ISO date string
  if (_DATE_OP_SET.has(f.op)) {
    const dv    = getDateValue(col, row)
    const today = _isoToday()
    const fv    = String(f.value || '')
    if (!dv && f.op !== 'is empty') return false
    switch (f.op) {
      case 'before':       return dv < fv
      case 'after':        return dv > fv
      case 'on':           return dv === fv
      case 'today':        return dv === today
      case 'last 7 days':  return dv >= _isoOffset(-6)  && dv <= today
      case 'last 30 days':   return dv >= _isoOffset(-29)       && dv <= today
      case 'month to date':  return dv >= _isoMonthStart()      && dv <= today
      case 'last month':     return dv >= _isoLastMonthStart()  && dv <= _isoLastMonthEnd()
      case 'quarter to date':return dv >= _isoQuarterStart()    && dv <= today
      case 'last 3 months':  return dv >= _isoNMonthsAgoStart(3) && dv <= _isoLastMonthEnd()
      case 'year to date':   return dv >= _isoYearStart()       && dv <= today
      case 'next 7 days':    return dv >= today                 && dv <= _isoOffset(6)
      case 'this week': {
        const dow = new Date().getDay()
        const mo  = -(dow === 0 ? 6 : dow - 1)
        return dv >= _isoOffset(mo) && dv <= _isoOffset(mo + 6)
      }
    }
  }

  // Numeric operators: compare parsed floats
  if (_NUMBER_OP_SET.has(f.op)) {
    const raw = getFilterableValue(col, row)
    const a = parseFloat(raw)
    const b = parseFloat(f.value)
    if (isNaN(a) || isNaN(b)) return false
    switch (f.op) {
      case '=':  return a === b
      case '!=': return a !== b
      case '>':  return a > b
      case '<':  return a < b
      case '>=': return a >= b
      case '<=': return a <= b
    }
  }

  const raw = getFilterableValue(col, row)
  const val = raw.toLowerCase().trim()
  const fv  = String(f.value).toLowerCase().trim()
  switch (f.op) {
    case 'contains':     return val.includes(fv)
    case 'is':           return val === fv
    case 'is not':       return val !== fv
    case 'starts with':  return val.startsWith(fv)
    case 'is empty':     return !raw.trim()
    case 'is not empty': return !!raw.trim()
    default:             return true
  }
}

/**
 * Shared reactive state for sort, filter, and column visibility.
 *
 * @param {Array}    COLUMNS    - Column definitions. Each entry may include:
 *                                 key, label, default, coming, tdClass, long,
 *                                 compute(row), filterValue(row), sortValue(row),
 *                                 dateValue(row),  ← for date operators
 *                                 ops (string[]), valueOptions (string[])
 * @param {string}   storageKey - localStorage key for persisting column settings
 * @param {Function} searchFn   - (row, lowerQuery) => boolean
 */
export function useTableControls(COLUMNS, storageKey, searchFn = null) {
  // ── localStorage persistence ──────────────────────────────────────────────
  const _allKeys     = COLUMNS.filter(c => !c.coming).map(c => c.key)
  const _defaultKeys = COLUMNS.filter(c => c.default && !c.coming).map(c => c.key)

  const _load = () => {
    try {
      const s = JSON.parse(localStorage.getItem(storageKey))
      if (s?.order && s?.visible) {
        const order   = [...s.order.filter(k => _allKeys.includes(k)), ..._allKeys.filter(k => !s.order.includes(k))]
        const visible = s.visible.filter(k => _allKeys.includes(k))
        return { order, visible }
      }
    } catch {}
    return { order: [..._allKeys], visible: [..._defaultKeys] }
  }

  const { order: _initOrder, visible: _initVisible } = _load()
  const columnOrder = ref(_initOrder)
  const visibleKeys = ref(_initVisible)

  watch([columnOrder, visibleKeys], () => {
    localStorage.setItem(storageKey, JSON.stringify({ order: columnOrder.value, visible: visibleKeys.value }))
  }, { deep: true })

  // ── Derived column lists ──────────────────────────────────────────────────
  const pickerColumns = computed(() =>
    columnOrder.value.map(k => COLUMNS.find(c => c.key === k)).filter(Boolean)
  )

  const activeColumns = computed(() =>
    columnOrder.value
      .filter(k => visibleKeys.value.includes(k))
      .map(k => COLUMNS.find(c => c.key === k))
      .filter(Boolean)
  )

  const toggleColumn = key => {
    visibleKeys.value = visibleKeys.value.includes(key)
      ? visibleKeys.value.filter(k => k !== key)
      : [...visibleKeys.value, key]
  }

  const resetColumns = () => {
    columnOrder.value = [..._allKeys]
    visibleKeys.value = [..._defaultKeys]
  }

  // ── Sort ──────────────────────────────────────────────────────────────────
  const sortKey = ref(null)
  const sortDir = ref('asc')

  const toggleSort = key => {
    if (sortKey.value === key) {
      sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortKey.value = key
      sortDir.value = 'asc'
    }
  }

  // ── Search + active filters ───────────────────────────────────────────────
  const searchQuery   = ref('')
  const activeFilters = ref([])

  const chipLabel = f => {
    const col   = COLUMNS.find(c => c.key === f.col)
    const label = col?.label || f.col
    if (NO_VALUE_OPS.has(f.op)) return `${label} ${f.op}`
    if (DATE_INPUT_OPS.has(f.op) && f.value) {
      try {
        const [y, m, d] = f.value.split('-').map(Number)
        const pretty = new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        return `${label} ${f.op} ${pretty}`
      } catch {}
    }
    return `${label} ${f.op} "${f.value}"`
  }

  const removeFilter = i => { activeFilters.value = activeFilters.value.filter((_, idx) => idx !== i) }
  const clearFilters  = () => { activeFilters.value = [] }

  // ── Core filter + sort function ───────────────────────────────────────────
  const filterRows = rows => {
    let list = [...rows]

    if (searchQuery.value.trim() && searchFn) {
      const q = searchQuery.value.toLowerCase()
      list = list.filter(row => searchFn(row, q))
    }

    for (const f of activeFilters.value) {
      const col = COLUMNS.find(c => c.key === f.col)
      if (col) list = list.filter(row => applyFilter(col, f, row))
    }

    if (sortKey.value) {
      const col = COLUMNS.find(c => c.key === sortKey.value)
      if (col) {
        const dir = sortDir.value === 'asc' ? 1 : -1
        list.sort((a, b) => {
          const av = getSortableValue(col, a)
          const bv = getSortableValue(col, b)
          return av < bv ? -dir : av > bv ? dir : 0
        })
      }
    }

    return list
  }

  return {
    // column state
    columnOrder, visibleKeys, activeColumns, pickerColumns,
    toggleColumn, resetColumns,
    // sort
    sortKey, sortDir, toggleSort,
    // search + filter
    searchQuery, activeFilters,
    chipLabel, removeFilter, clearFilters,
    // core fn
    filterRows,
  }
}
